import { useMemo, useReducer } from "react";

import { JobsApi } from "@common/api/v1.1/actions/JobsApi";
import { UsersApi } from "@common/api/v1.1/actions/UsersApi";
import {
  ApiJob,
  ApiJobContent,
  ApiJobNote,
  JobEvent,
  JobWarning,
  ListedJob,
} from "@common/api/v1.1/types/jobs";
import { Actions, AuthState, DEFAULT_UI_STATE, JobState, Message, State } from "../types";
import { abortJob, AbortJobAction } from "./abortJob";
import { toggleActiveKey, ToggleActiveKeyAction } from "./activeKey";
import { clearActiveJob, ClearActiveJobAction } from "./clearActiveJob";
import {
  loadJobFailed,
  LoadJobFailedAction,
  loadJobStart,
  LoadJobStartAction,
  loadJobSuccess,
  LoadJobSuccessAction,
} from "./loadJob";
import {
  loginRequestFailed,
  LoginRequestFailedAction,
  loginUserByFormStart,
  LoginUserByFormStartAction,
  loginUserByTokenStart,
  LoginUserByTokenStartAction,
  loginUserSuccess,
  LoginUserSuccessAction,
  logout,
  LogoutAction,
} from "./login";
import { pushMessage, PushMessageAction, removeMessage, RemoveMessageAction } from "./messages";
import { removeJob, RemoveJobAction } from "./removeJob";
import { closeSidebar, CloseSidebarAction, toggleSidebar, ToggleSidebarAction } from "./sidebar";
import { updateJobs, UpdateJobsAction } from "./updateJobs";
import { setActiveJob, SetActiveJobAction } from "./setActiveJob";

let messageId = 1;

type ReducerAction =
  | AbortJobAction
  | ClearActiveJobAction
  | CloseSidebarAction
  | LoadJobFailedAction
  | LoadJobSuccessAction
  | LoadJobStartAction
  | LoginUserByFormStartAction
  | LoginUserByTokenStartAction
  | LoginUserSuccessAction
  | LoginRequestFailedAction
  | LogoutAction
  | PushMessageAction
  | RemoveJobAction
  | RemoveMessageAction
  | ToggleActiveKeyAction
  | ToggleSidebarAction
  | UpdateJobsAction
  | SetActiveJobAction
const reducer = (state: State, action: ReducerAction): State => {
  switch (action.type) {
    case "ABORT JOB":
      return abortJob(state, action);
    case "SET ACTIVE JOB":
      return setActiveJob(state, action)
    case "CLEAR ACTIVE JOB":
      return clearActiveJob(state, action);
    case "CLOSE SIDEBAR":
      return closeSidebar(state, action);
    case "LOAD JOB FAILED":
      return loadJobFailed(state, action);
    case "LOAD JOB SUCCESS":
      return loadJobSuccess(state, action);
    case "LOAD JOB START":
      return loadJobStart(state, action);
    case "LOGIN USER BY FORM START":
      return loginUserByFormStart(state, action);
    case "LOGIN USER BY TOKEN START":
      return loginUserByTokenStart(state, action);
    case "LOGIN USER SUCCESS":
      return loginUserSuccess(state, action);
    case "LOGIN REQUEST FAILED":
      return loginRequestFailed(state, action);
    case "LOGOUT":
      return logout(state, action);
    case "PUSH MESSAGE":
      return pushMessage(state, action);
    case "REMOVE JOB":
      return removeJob(state, action);
    case "REMOVE MESSAGE":
      return removeMessage(state, action);
    case "TOGGLE ACTIVE KEY":
      return toggleActiveKey(state, action);
    case "TOGGLE SIDEBAR":
      return toggleSidebar(state, action);
    case "UPDATE JOBS":
      return updateJobs(state, action);
  }
};
export const useWorkbenchState = (blockingStep: string): [State, Actions] => {
  const [state, dispatch] = useReducer(reducer, DEFAULT_UI_STATE);
  const actions = useMemo(
    (): Actions => ({
      abortJob: async (jobId: string) => {
        const jobApi = new JobsApi(state.auth);
        await jobApi.checkinJob(jobId, "aborted");
        dispatch({ jobId, type: "ABORT JOB" });
      },
      addNote: async (jobId: string, content: string) => {
        const jobApi = new JobsApi(state.auth);
        await jobApi.addNote(jobId, content);
      },
      clearActiveJob: () => {
        dispatch({ type: "CLEAR ACTIVE JOB" });
      },
      setActiveJob: (id: string) => {
        dispatch({ type: "SET ACTIVE JOB" , id:id});
      },
      closeSidebar: () => {
        dispatch({ type: "CLOSE SIDEBAR" });
      },
      deleteNote: async (jobId: string, noteId: string) => {
        const jobApi = new JobsApi(state.auth);
        await jobApi.deleteNote(jobId, noteId);
      },
      fetchNotes: async (jobId: string): Promise<Array<ApiJobNote>> => {
        const jobApi = new JobsApi(state.auth);
        return jobApi.fetchNotes(jobId);
      },
      hideMessage: (message: Message) => {
        dispatch({
          message,
          type: "REMOVE MESSAGE",
        });
      },
      loadJob: async (jobId: string, listRoles: Array<string>, review: boolean): Promise<void> => {
        dispatch({ jobId, type: "LOAD JOB START" });
        try {
          const jobsApi = new JobsApi(state.auth);
          let job: ApiJob;
          if (review) {
            job = await jobsApi.getJob(jobId);
          } else {
            try {
              job = await jobsApi.checkoutJob(jobId);
            } catch (e) {
              if (e.source.message === "Unable to checkout job.") {
                // Job is checked out by somebody else, navigate to read-only version of WB
                // note : doesn't work on local since the review version isn't actually running
                // and the URL is different.  This is kind of a hack, but changing the "review" boolean
                // will be quite involved. 
                const url = window.location.href;
                const modifiedUrl = url.replace(/\/(zoning|reading|mapping|splitting)\//g, '/$1-review/');
                if (modifiedUrl != url) {
                  window.location.href = modifiedUrl;
                } 
              }
              throw e;
            }
          }
          
          const rawContents = await Promise.all(
            
            listRoles.map(
              async (role: string): Promise<Array<ApiJobContent>> => {

                let results: Array<ApiJobContent> = [];
                let total = -1;
                while (total < 0 || results.length < total) {
                  try {
                    let parentFiles = ["zoning-page-png", "zoning-pagehr-png", "zoning-pagesmeta-json"];
                    let targetJobId = jobId;
                    if (parentFiles.includes(role) && job.parent_id) {
                      targetJobId = job.parent_id;
                    }
                    const result = await jobsApi.listJobContentForRole(
                      targetJobId,
                      role,
                      100,
                      results.length,
                      false,
                    );
                    if (result.total_count === 0) {
                      return [];
                    }
                    if (result.total_count !== total) {
                      if (total < 0) {
                        total = result.total_count;
                      } else {
                        throw new Error("Total count changed while fetching job content list.");
                      }
                    }
                    results = results.concat(result.list);
                  } catch (err) {
                    total = -1;
                    results = [];
                  }
                }

                return results;
              },
            ),
          );
          
          const contents = rawContents.reduce((prev, next) => prev.concat(next), []);
          
          dispatch({ contents, job, type: "LOAD JOB SUCCESS" });
        } catch (error) {
          dispatch({ error, jobId, type: "LOAD JOB FAILED" });
        }
      },
      loginByForm: async (email: string, password: string) => {
        try {
          dispatch({ email, password, type: "LOGIN USER BY FORM START" });
          const usersApi = new UsersApi({
            email,
            password,
            state: AuthState.loggingInByForm,
          });
          const user = await usersApi.login(email, password);
          const token = user.api_keys.live || user.api_keys.test || null;
          if (!token) {
            throw new Error("Unable to login. Please verify your account.");
          }
          const authString = window.btoa(`${token}:`);
          dispatch({ authString, token, type: "LOGIN USER SUCCESS", user });
        } catch (err) {
          if (err.message === 'Failed to fetch'){
            dispatch({ type: "LOGIN REQUEST FAILED" });
            throw err;
          } else {
            dispatch({ type: "LOGOUT" });
            throw err;
          }
        }
      },
      loginWithToken: async (token: string) => {
        try {
          const authString = window.btoa(`${token}:`);
          dispatch({ authString, token, type: "LOGIN USER BY TOKEN START" });
          const usersApi = new UsersApi({
            authString,
            state: AuthState.loggingInByStorage,
            token,
          });
          const user = await usersApi.getCurrentUser();
          dispatch({ authString, token, type: "LOGIN USER SUCCESS", user });
        } catch (err) {
          console.error(err);
          dispatch({ type: "LOGOUT" });
        }
      },
      resolveWarning: async (warning: JobWarning) => {
        if (state.job.state === JobState.ready) {
          if (!warning.message) {
            delete warning.message;
          }
          if (!warning.dataPoint) {
            delete warning.dataPoint;
          }
          warning.resolutionDate = new Date();
          const jobApi = new JobsApi(state.auth);
          await jobApi.pushWarning(state.job.job.id, warning);
        }
      }, 
      logout: () => {
        dispatch({ type: "LOGOUT" });
      },
      loginRequestFailed: () => {
        dispatch({ type: "LOGIN REQUEST FAILED" });
      },
      refreshCheckout: async () => {
        if (state.job.state === JobState.ready) {
          const jobApi = new JobsApi(state.auth);
          await jobApi.checkoutJob(state.job.job.id);
        }
      },
      removeJobFromList: (jobId: string) => {
        dispatch({ jobId, type: "REMOVE JOB" });
      },
      showMessage: (
        content: React.ReactElement,
        title: string,
        displaySeconds: Message["displaySeconds"],
        icon: Message["icon"],
      ) => {
        dispatch({
          message: {
            content,
            displaySeconds,
            icon,
            id: `${messageId++}`,
            title,
          },
          type: "PUSH MESSAGE",
        });
      },
      toggleActiveKey: () => {
        // show the user a confirmation prompt before toggling the active key if they have a job
        // loaded, loading, etc
        if (
          state.job.state !== JobState.initial &&
          !window.confirm(
            "Are you sure you want to switch the live key? You will lose any unsaved changes for the current job.",
          )
        ) {
          return;
        }
        dispatch({ type: "TOGGLE ACTIVE KEY" });
      },
      toggleSidebar: () => {
        dispatch({ type: "TOGGLE SIDEBAR" });
      },
      updateJobs: (jobs: Array<ListedJob>, eventType: JobEvent | null) => {
        dispatch({ blockingStep, eventType, jobs, type: "UPDATE JOBS" });
      },
      updateNote: async (jobId: string, noteId: string, content: string): Promise<ApiJobNote> => {
        const jobApi = new JobsApi(state.auth);
        return jobApi.updateNote(jobId, noteId, content);
      },
    }),
    [state, dispatch],
  );
  return [state, actions];
};
